import {
  Component,
  ChangeDetectionStrategy,
  computed,
  input,
  signal,
  OnInit
} from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * A single grading rule rendered in the disclosure body.
 *
 * `id` — stable identifier for the rule, used in templates and tests
 * `title` — short bold lead-in (e.g. "Case-sensitive")
 * `body` — plain-English explanation that follows the title
 */
export interface GradingRule {
  id:
    | 'case-sensitive'
    | 'whitespace-strict'
    | 'whitespace-trimmed'
    | 'ignore-internal-whitespace';
  title: string;
  body: string;
}

/**
 * Collapsible "How will this be graded?" alert for short-answer quiz questions.
 *
 * Renders as a DaisyUI alert-info panel that lists the active grading rules
 * for a short-answer question (case sensitivity + whitespace handling).
 *
 * If both rules are at their forgiving defaults (caseSensitive=false,
 * trimWhitespace=true), the component renders nothing because there is
 * nothing useful to disclose.
 *
 * Used in three views (KAN-157):
 *   1. Learner quiz panel (collapsed by default)
 *   2. Course Creator preview modal (expanded by default)
 *   3. Content Hub quiz view modal (expanded by default)
 */
@Component({
  selector: 'app-grading-rules-alert',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './grading-rules-alert.component.html',
  styleUrls: ['./grading-rules-alert.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GradingRulesAlertComponent implements OnInit {
  readonly caseSensitive = input<boolean>(false);
  readonly trimWhitespace = input<boolean>(true);
  readonly ignoreInternalWhitespace = input<boolean>(false);
  readonly defaultExpanded = input<boolean>(false);

  readonly expanded = signal<boolean>(false);

  readonly activeRules = computed<GradingRule[]>(() => {
    const rules: GradingRule[] = [];

    if (this.caseSensitive()) {
      rules.push({
        id: 'case-sensitive',
        title: 'Case-sensitive',
        body: 'typing "paris" instead of "Paris" will be marked wrong.'
      });
    }

    if (!this.trimWhitespace()) {
      rules.push({
        id: 'whitespace-strict',
        title: 'Whitespace matters',
        body: 'leading and trailing spaces will count.'
      });
    }

    if (this.ignoreInternalWhitespace()) {
      rules.push({
        id: 'ignore-internal-whitespace',
        title: 'Ignore internal whitespace',
        body: '"e = mc2" and "e=mc2" will be treated as the same answer.'
      });
    }

    return rules;
  });

  readonly ruleCountLabel = computed<string>(() => {
    const count = this.activeRules().length;
    return `${count} ${count === 1 ? 'rule' : 'rules'}`;
  });

  ngOnInit(): void {
    this.expanded.set(this.defaultExpanded());
  }

  toggle(): void {
    this.expanded.update(v => !v);
  }
}
